import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);  useEffect(() => {
    if (isAuthenticated && user) {
      setIsSubmitting(false); // Reset submitting state on successful login
      // Redirect based on user role
      switch (user.role) {
        case 'manager':
          navigate('/dashboard');
          break;
        case 'executive1':
          navigate('/orders');
          break;
        case 'executive2':
          navigate('/stock-entry');
          break;
        case 'worker':
          navigate('/picking');
          break;
        default:
          navigate('/dashboard');
      }    }  }, [isAuthenticated, user, navigate]);  // Reset submitting state when Redux loading completes
  useEffect(() => {
    if (!loading && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [loading, isSubmitting]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      return;
    }    try {
      setIsSubmitting(true);
      
      const result = await dispatch(loginUser({ username: username.trim(), password }));
      
      // Check if the login was rejected
      if (loginUser.rejected.match(result)) {
        // Don't show loading for failed attempts
        setIsSubmitting(false);
      }
    } catch (err) {
      // Stop loading on any error
      setIsSubmitting(false);
      console.error('Login error:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <LogIn className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Store Management System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your account
          </p>        </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error ? (
            <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4 mb-6 shadow-md">
              <div className="flex">
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-base font-semibold text-red-800">Login Failed</h3>
                  <p className="text-sm text-red-700 mt-1">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>              <input
                id="username"
                name="username"
                type="text"
                required
                value={username}                onChange={(e) => {
                  setUsername(e.target.value);
                  // Clear error when user starts typing
                  if (error) {
                    dispatch(clearError());
                  }
                  if (isSubmitting) {
                    setIsSubmitting(false);
                  }
                }}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your username"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}                  onChange={(e) => {
                    setPassword(e.target.value);
                    // Clear error when user starts typing
                    if (error) {
                      dispatch(clearError());
                    }
                    if (isSubmitting) {
                      setIsSubmitting(false);
                    }
                  }}
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>          <div>
            <button
              type="submit"
              disabled={isSubmitting || !username.trim() || !password.trim()}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
